import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageSquare, Mail, Phone, Globe, ShieldCheck } from 'lucide-react';

const PHONE_DISPLAY = '(305) 432-3557';
const PHONE_TEL = '+13054323557';

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h2 className="text-xl md:text-2xl font-bold text-slate-900">{title}</h2>
    <div className="text-slate-600 leading-relaxed space-y-3 text-base">{children}</div>
  </div>
);

export const SmsTermsPage = () => {
  useEffect(() => {
    document.title = 'SMS Terms & Conditions | Calyvra Insurance';
  }, []);

  return (
    <div className="overflow-hidden" data-testid="sms-terms-page">
      <section className="gradient-hero py-16 lg:py-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 rounded-full mb-6">
              <MessageSquare className="h-4 w-4 text-sky-600" />
              <span className="text-sm font-medium text-sky-700">Text Messaging</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">SMS Terms &amp; Conditions</h1>
            <p className="text-slate-600 text-lg">
              By opting into SMS from a web form or other medium, you agree to receive text messages from
              Calyvra Insurance Agency.
            </p>
            <p className="text-sm text-slate-400 mt-4">Last updated: June 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-slate-100 shadow-sm p-8 md:p-12 space-y-10">

            <Section title="Messages You May Receive">
              <p>Messages may include:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Insurance application updates</li>
                <li>Appointment reminders</li>
                <li>Requests for documentation</li>
                <li>Customer support</li>
                <li>ACA enrollment assistance</li>
                <li>Medicare enrollment assistance</li>
                <li>Premium Assistance Program communications</li>
                <li>Policy status updates</li>
              </ul>
            </Section>

            <Section title="Message Frequency & Rates">
              <ul className="list-disc pl-6 space-y-2">
                <li>Message frequency may vary.</li>
                <li>Message and data rates may apply.</li>
              </ul>
            </Section>

            <Section title="Opt-Out & Help">
              <ul className="list-disc pl-6 space-y-2">
                <li>Reply <span className="font-semibold text-slate-800">STOP</span> to unsubscribe.</li>
                <li>Reply <span className="font-semibold text-slate-800">HELP</span> for assistance.</li>
              </ul>
              <div className="rounded-xl bg-sky-50 border border-sky-100 p-4 flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-sky-600 mt-0.5 flex-shrink-0" />
                <p className="text-slate-700 text-sm">
                  Consent to receive SMS messages is optional and is not a condition of purchase. Your SMS consent
                  is not shared with third parties or affiliates for marketing purposes.
                </p>
              </div>
            </Section>

            <Section title="Contact & Assistance">
              <div className="rounded-xl bg-slate-900 text-white p-6 space-y-3">
                <p className="text-slate-300">For assistance call <a href={`tel:${PHONE_TEL}`} className="text-white font-semibold hover:underline">{PHONE_DISPLAY}</a>.</p>
                <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors">
                  <Phone className="h-5 w-5 text-sky-400" /> {PHONE_DISPLAY}
                </a>
                <a href="mailto:info@calyvrainsurance.com" className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors">
                  <Mail className="h-5 w-5 text-sky-400" /> info@calyvrainsurance.com
                </a>
                <a href="https://calyvrainsurance.com" className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors">
                  <Globe className="h-5 w-5 text-sky-400" /> https://calyvrainsurance.com
                </a>
              </div>
            </Section>

            <div className="pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                View our{' '}
                <Link to="/privacy-policy" className="text-sky-600 hover:underline font-medium">Privacy Policy</Link>{' '}
                at{' '}
                <a href="https://calyvrainsurance.com/privacy-policy" className="text-sky-600 hover:underline">
                  https://calyvrainsurance.com/privacy-policy
                </a>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SmsTermsPage;
